import { POLICIES } from '../policies';
import {
  INTERCOM,
  GORGIAS,
  ZENDESK,
  DESK,
  MESSENGER,
  SALESFORCE,
  SALESFORCE_CHAT,
  GLADLY,
  validCommunicationType,
  GENESYS,
  AMAZON_CONNECT,
  TWILIO_VOICE,
} from '../constants/conversationTypes';
import { formatFromChannel, maskEmailAddress, getSalesforceTicketDisplayName } from './formatUtil';
import { isAnswered } from '../selectors/tickets';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import last from 'lodash/last';
import escapeRegExp from 'lodash/escapeRegExp';
import moment from 'moment';
import { DEFAULT_FIRST_GREETING, DEFAULT_SECOND_GREETING, FIRST_NAME_PLACEHOLDER } from '../constants/fixedMessages';
import xss from 'xss';
import regex from '../constants/regex';
import { handleError } from 'services/ErrorService/ErrorService';
import isEmpty from 'lodash/isEmpty';
import { getExpertById } from 'api/expertQueries';
import { AVAILABLE } from 'constants/expertAvailability';
import { TICKET_STATUS_FILTER } from 'constants/ticketStatuses';
import { isLiveChat, isAsyncChat, isDelayedAsyncChat, isVoiceCallChat } from 'util/chat';
import { getSessionToken } from 'util/parseSelectors';
import { ALLOWED_TEXT_SYMBOLS } from '../constants/emoji';
import { safePlayAudio } from './audioUtil';

const KNOWN_SMB_NAMES = Object.keys(POLICIES);

export const getSmbName = (state, restrictToKnown = true) => {
  let selectedTicket = {};
  const assignedTickets = state.tickets.assignedTickets;
  if (state.selectedTicketId) {
    selectedTicket = Object.assign({}, assignedTickets[state.selectedTicketId]);
  } else if (assignedTickets.allTickets.length > 0) {
    const ticketId = assignedTickets.allTickets[0];
    selectedTicket = Object.assign({}, assignedTickets[ticketId]);
  }

  if (restrictToKnown && !KNOWN_SMB_NAMES.includes(selectedTicket.smbName)) {
    return 'generic';
  }

  return selectedTicket.smbName || 'generic';
};

export const isParsedJson = (string) => {
  try {
    JSON.parse(string);
  } catch (e) {
    return true;
  }

  return false;
};

export const showNotification = (title, body, audioFile, onClick, { requireInteraction = false } = {}) => {
  if (window.Notification !== undefined) {
    const createNotification = () => {
      const notification = new Notification(title, {
        body,
        icon: '/images/notification-icon.png',
        requireInteraction,
      });

      if (isFunction(onClick)) {
        notification.onclick = (e) => {
          onClick();
          notification.close();
        };
      }

      if (audioFile) {
        const audio = new Audio(audioFile);
        safePlayAudio(audio);
      }
    };

    if (Notification.permission === 'default') {
      Notification.requestPermission().then(createNotification);
    } else if (Notification.permission === 'granted') {
      createNotification();
    }
  }
};

// deprecated - use connectToGrammarly
export const getTextFromGrammarly = (className) => {
  let textElementArray = document.querySelectorAll(className);
  let text = '';
  for (let i = 0; i < textElementArray.length; ++i) {
    text += textElementArray[i].innerText;
  }
  text = text.trim();

  return text;
};

export const capitalizeFirstLetter = (text) => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const normalizeSnakeCase = (string) => {
  if (typeof string !== 'string') {
    return string;
  }
  const lowerCasedString = string.toLowerCase().split('_').join(' ');

  return lowerCasedString.slice(0, 1).toUpperCase() + lowerCasedString.slice(1);
};

export const convertStringToSnakeCase = (string) => {
  if (typeof string !== 'string') {
    return '';
  }

  return string.toLowerCase().split(' ').join('_');
};

export const expertIsAvailable = async (expertId) => {
  let expertAvailability;
  try {
    const expert = await getExpertById(expertId);
    expertAvailability = expert?.availability;
  } catch (err) {
    throw new Error(`There was a problem collecting Expert availability info.`);
  }

  return expertAvailability === AVAILABLE;
};

export const sortExpertsByName = (experts) =>
  sortBy(experts, [({ firstName }) => firstName.toLowerCase(), ({ lastName }) => lastName.toLowerCase()]);

/**
 * taken from here: https://stackoverflow.com/questions/7745867/how-do-you-get-the-cursor-position-in-a-textarea
 * @param {*} input: MDN text element
 * Returns the start and end cursor positions
 */
const _getCursorPos = (input) => {
  if ('selectionStart' in input) {
    return {
      start: input.selectionStart,
      end: input.selectionEnd,
    };
  } else if (input.createTextRange) {
    let sel = document.selection.createRange();
    if (sel.parentElement() === input) {
      let rng = input.createTextRange();
      rng.moveToBookmark(sel.getBookmark());
      let len;
      for (len = 0; rng.compareEndPoints('EndToStart', rng) > 0; rng.moveEnd('character', -1)) {
        len++;
      }
      rng.setEndPoint('StartToStart', input.createTextRange());
      let pos;
      for (pos = { start: 0, end: len }; rng.compareEndPoints('EndToStart', rng) > 0; rng.moveEnd('character', -1)) {
        pos.start++;
        pos.end++;
      }

      return pos;
    }
  }

  return { start: -1, end: -1 };
};

/**
 *
 * @param {*} input: MDN text element
 * @param {*} insertText: The text you want to insert
 * Return the text after inserting text into an input based off the cursor position
 */
export const getTextAfterReplacementAtCursor = (input, insertText) => {
  const oldValue = input.value;
  const { start, end } = _getCursorPos(input);
  // if we cannot find the cursor, just add the text at the end
  if (start === -1 && end === -1) {
    return oldValue + insertText;
  }
  const startStr = oldValue.slice(0, start);
  const endStr = oldValue.slice(end);

  // return the text instead of modifying the input
  return startStr + insertText + endStr;
};

/**
 * removeUndefines removes any keys which have an undefined value
 * Note this mutates objects intentionally
 * @param {*} obj Any JS object
 */
export const removeUndefines = (obj) => {
  if (Array.isArray(obj) || typeof obj !== 'object') {
    return obj;
  }
  const keys = Object.keys(obj);
  keys.forEach((key) => {
    const val = obj[key];
    if (val === undefined) {
      delete obj[key];
    }
  });

  return obj;
};

export const getLastPageUrl = (selectedTicket) => get(selectedTicket, 'ticket.extraTicketInfo[0].lastPageUrl', null);

/**
 * getMatchingBlacklistedPhrases returns the first matching blacklisted phrase used in the message
 * @param {*} messageText: Text that Expert wants to send as a message
 * @param {*} words: BlacklistWords from the Parse
 * @param {*} regexes: BlacklistRegexes from the Parse
 */
export const getMatchingBlacklistedPhrases = (messageText, words = [], regexes = {}, cussWords = []) => {
  let blacklistedRegexes = [
    /\[(.*?)\]/g,
    /{(.*?)\}/g,
    /customer\.first_name/g,
    /CUSTOMER NAME/g,
    /INSERT [A-Z 0-9]+/g,
    /PASTEAMAZONORDERIDHERE/gi,
    /(\w+:\/\/)?drive\.google\.com(\S*)/gi,
    /SHIPPING SERVICES\s+\*SHIP BY\*\s+DELIVER BY/g,
    /\*DATE\*/g,
    /â€\S*/g, // matches â€ and any non-space characters that follow
    /have you understood and addressed your customer's main question/, // partial fragment of default text
  ];

  // find a blacklist item in the messageText from all of the above if any
  let wordMatch = findRegexMatch(messageText, blacklistedRegexes);
  if (wordMatch) {
    return wordMatch;
  }

  // ignore any urls
  const reg = new RegExp(
    '(https?:\\/\\/)?(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{2,256}\\.[a-z]{2,20}\\b([-a-zA-Z0-9@:%_\\+.~#()?&//=]*)',
    'g'
  );
  messageText = messageText.replace(reg, '');

  // find a blacklist tiem in the messageText from either blacklistWords or blacklistRegexes if any
  blacklistedRegexes = getRegexFromParse(regexes);

  return (
    findRegexMatch(messageText, blacklistedRegexes) ||
    findWordMatchFromParse(messageText, words) ||
    findCussWordMatchFromParse(messageText, cussWords)
  );
};

/**
 * findWordMatchFromParse returns the first matching blacklisted word used in the message
 * @param {*} messageText: Text that Expert wants to send as a message
 * @param {*} words: BlacklistWords from the Parse
 */
const findWordMatchFromParse = (messageText, words) => {
  const lowercaseMessageText = messageText.toLowerCase();
  let wordMatched = null;
  if (Array.isArray(words)) {
    words.some((word) => {
      const wordToTest = word && word.toLowerCase();
      const isMatch = !!wordToTest && lowercaseMessageText.includes(wordToTest);
      if (isMatch) {
        wordMatched = word;
      }

      return isMatch;
    });
  }

  return wordMatched;
};

/**
 * findCussWordMatchFromParse returns the first matching blacklisted cuss word used in the message
 * @param {*} messageText: Text that Expert wants to send as a message
 * @param {*} cussWords: BlacklistedCussWords from the Parse
 */
const findCussWordMatchFromParse = (messageText, cussWords) => {
  const lowerCaseMessageText = messageText.toLowerCase();
  const lowerCaseArr = lowerCaseMessageText.split(' ');
  for (let i = 0; i < lowerCaseArr.length; i++) {
    const matchFound = cussWords.indexOf(lowerCaseArr[i]);
    if (matchFound >= 0) {
      return lowerCaseArr[i];
    }
  }

  return null;
};

/**
 * getRegexFromParse returns the list of blacklist javascript RegExp
 * @param {*} regexes: BlacklistRegexes from the Parse
 */
const getRegexFromParse = (regexes) => {
  const regexesList = [];
  for (let key in regexes) {
    const regexToMatch = new RegExp(key, regexes[key]);
    regexesList.push(regexToMatch);
  }

  return regexesList;
};

/**
 * calculateRemaingingTime returns the remaining time from an expiration date
 * @param {*} expirationTime:
 */
export const calculateRemainingTime = (expirationTime) => {
  if (expirationTime) {
    const currentTime = new Date().getTime();
    const expirationTimeTime = new Date(expirationTime).getTime();

    const remainingTime = expirationTimeTime - currentTime;

    const rawMinutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));
    const rawSeconds = Math.floor((remainingTime % (1000 * 60)) / 1000);

    const minutes = rawMinutes >= 10 ? `${rawMinutes}` : `0${rawMinutes}`;
    const seconds = rawSeconds >= 10 ? `${rawSeconds}` : `0${rawSeconds}`;

    return { minutes, seconds, rawSeconds, rawMinutes };
  }

  return -1;
};

/**
 * findRegexMatch returns the first matching regex in the messageText
 * @param {*} messageText: Text that Expert wants to send as a message
 * @param {*} regexes: a list of RegExp
 */
const findRegexMatch = (messageText, regexes) => {
  if (!messageText) {
    return null;
  }

  let matchFound;
  for (let i = 0; i < regexes.length; i++) {
    const regexToMatch = regexes[i];
    matchFound = messageText.match(regexToMatch);
    if (matchFound && matchFound.length >= 1) {
      return matchFound[0];
    }
  }

  return null;
};

export const checkHasGreeting = (messageText) => {
  if (!messageText) {
    return false;
  }

  const regexes = [DEFAULT_FIRST_GREETING, DEFAULT_SECOND_GREETING].map((fullStr) => {
    // escape backslashes
    fullStr = fullStr.replace(/\\/g, '\\\\');
    // replace blacklisted text with regex
    // source: https://stackoverflow.com/questions/20690499/concrete-javascript-regex-for-accented-characters-diacritics
    const nameRegex = '[\\w\u00C0-\u024F\u1E00-\u1EFF]+';
    // allow name to be one or two words
    fullStr = fullStr.replace(FIRST_NAME_PLACEHOLDER, `${nameRegex}( ${nameRegex})?`);
    // fix period since that has a meaning in regexes
    fullStr = fullStr.replace(/\./g, '\\.');
    // allow whitespaces before new line
    fullStr = fullStr.replace(/<\/p>/g, '(</p>\\s*<p>|<br>\\s*</p>\\s*<p>|<br>\\s*<br>|</p>)');

    return new RegExp(fullStr);
  });

  return !!findRegexMatch(messageText, regexes);
};

export const getUniqueIdOfSearchResultAndType = (result, resultType) => `${resultType}_${result.objectID}`;

export const getTimeSortFieldFromStatusFilter = (ticketStatus) => {
  if ([TICKET_STATUS_FILTER.closed, TICKET_STATUS_FILTER.forwardToExperts].includes(ticketStatus)) {
    return 'createdAt';
  }

  return 'conversationUpdatedAt';
};

export const shouldUseFifoOrder = (ticketStatus) => {
  return [
    TICKET_STATUS_FILTER.forwardToExperts,
    TICKET_STATUS_FILTER.awaitingNomination,
    TICKET_STATUS_FILTER.escalated,
  ].includes(ticketStatus);
};

export const shouldCopyMacro = (selectedInboxTab) => {
  return ['assigned', 'live_chat'].indexOf(selectedInboxTab) > -1;
};

export const shouldUseDummyButton = (selectedInboxTab) => {
  return ['assigned', 'live_chat'].indexOf(selectedInboxTab) > -1;
};

export const isJSON = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }

  return true;
};

export const stringContainedInTextList = (text, textList) => {
  if (!text || !textList) {
    return false;
  }

  return textList.some((foundText) => foundText && foundText.indexOf(text) > -1);
};

export const removePrefixFromString = (origString, prefix) => {
  if (origString.indexOf(prefix) !== 0) {
    return; // original implementation returns undefined if no prefix found;
  }

  return origString.replace(prefix, '');
};

/**
 * Gets the config field value for the account and brand if the brand exists on the config
 *
 * @param {object} businessDetails Business details object
 * @param {string} configField The config field to retrieve the value for
 * @param {string} brandName The name of the brand
 * @returns {unknown} Value of the config field
 */
export const getAccountBrandConfigValue = (
  businessDetails,
  configField,
  brandName,
  configFieldBrandLevel = configField
) => {
  const extraBusinessInfo = businessDetails.extraBusinessInfo;
  const accountValue = get(extraBusinessInfo, configField, false);
  const value = get(extraBusinessInfo, ['brandConfigs', brandName, configFieldBrandLevel], accountValue);

  return value;
};

// used for queue component - includes simplrTicketNumber
export const determineTicketDisplayName = (ticket, isAdmin, showEmail = false) => {
  const { ticketType, ticketId, customerFirstName, fromChannel, customerEmail, platformTicketId, simplrTicketNumber } =
    ticket;
  const SYSTEMS_TO_DISPLAY = [ZENDESK, DESK, INTERCOM, GORGIAS, MESSENGER, GLADLY, TWILIO_VOICE];

  let displayName = '';

  if (SYSTEMS_TO_DISPLAY.indexOf(ticketType) !== -1 && isAdmin && customerFirstName) {
    displayName = customerFirstName;
  } else if (SYSTEMS_TO_DISPLAY.indexOf(ticketType) !== -1 && isAdmin) {
    displayName = simplrTicketNumber;
  } else if (ticketType === SALESFORCE || ticketType === SALESFORCE_CHAT) {
    displayName = getSalesforceTicketDisplayName(customerFirstName, isAdmin, platformTicketId);
  } else {
    displayName =
      customerFirstName ||
      formatFromChannel(ticketType, fromChannel) ||
      `${(ticketType || '').toLowerCase()}:${ticketId}`;
  }

  if (showEmail && customerEmail) {
    displayName += ` (${customerEmail})`;
  }

  return displayName;
};

// used for header and notifications - no ticket number and longer data for specialists
export const determineTicketDisplayNameForHeader = (ticket, isAdmin, showEmail = false) => {
  const { ticketId, ticketType, customerFirstName, fromChannel, customerEmail, platformTicketId } = ticket;

  let displayName = '';

  const SYSTEMS_TO_DISPLAY = [AMAZON_CONNECT, DESK, GENESYS, GLADLY, GLADLY, GORGIAS, INTERCOM, MESSENGER, ZENDESK];
  if (SYSTEMS_TO_DISPLAY.indexOf(ticketType) !== -1 && isAdmin && customerFirstName) {
    if (fromChannel && customerFirstName) {
      displayName = `${customerFirstName} (${fromChannel})`;
    } else if (customerFirstName) {
      displayName = customerFirstName;
    } else if (fromChannel) {
      displayName = fromChannel;
    }
  } else if (SYSTEMS_TO_DISPLAY.indexOf(ticketType) !== -1 && isAdmin) {
    displayName = fromChannel;
  } else if (ticketType === SALESFORCE || ticketType === SALESFORCE_CHAT) {
    displayName = getSalesforceTicketDisplayName(customerFirstName, isAdmin, platformTicketId);
  } else {
    displayName =
      customerFirstName ||
      formatFromChannel(ticketType, fromChannel) ||
      `${(ticketType || '').toLowerCase()}:${ticketId}`;
  }

  if (showEmail && customerEmail) {
    displayName += ` (${customerEmail})`;
  }

  return displayName;
};

// for tickets in the format "Conversation with user@domain.com", we mask the email address
export const determineTicketSubject = (ticket) => {
  const { ticketType, subject } = ticket;
  if (!subject) {
    return '';
  }

  if (ticketType !== GORGIAS) {
    return subject;
  }

  const GORGIAS_SUBJECT_PREFIX = 'Conversation with';

  if (!subject.startsWith(GORGIAS_SUBJECT_PREFIX)) {
    return subject;
  }

  if (subject.startsWith(`${GORGIAS_SUBJECT_PREFIX} Customer`)) {
    return subject;
  }

  const potentialEmailAddress = subject.slice(GORGIAS_SUBJECT_PREFIX.length + 1);
  const maskedEmailAddress = maskEmailAddress(potentialEmailAddress);

  return maskedEmailAddress ? `${GORGIAS_SUBJECT_PREFIX} ${maskEmailAddress(maskedEmailAddress)}` : subject;
};

export const readTextFile = (file) => {
  const reader = new FileReader();

  return new Promise((resolve) => {
    reader.addEventListener(
      'load',
      () => {
        resolve(reader.result);
      },
      false
    );
    reader.readAsText(file);
  });
};

// TODO avoid masking non-coupon code matches
export const replaceCouponCodeWithMask = (text, couponCodeText) => {
  return text.replace(new RegExp(escapeRegExp(couponCodeText), 'g'), '****');
};

export const sleep = async (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const getPaddedTextOfUnsentMessageFromDom = () => {
  const textAreaId = 'largeMessageTextArea';
  const textAreaEl = document.getElementById(textAreaId);
  if (!textAreaEl) {
    return '';
  }
  let text = textAreaEl.value;
  if (text && last(text) !== '\n') {
    text += '\n';
  }

  return text;
};

export const isDayLessThanTresholdAgo = (dateStr, numDays) => {
  const date = moment(dateStr);
  date.add(numDays, 'days');

  // see if date is after current time after adding num days
  return date.isAfter();
};

export const itemIsUPSSurepost = (shippoInfo) => {
  return (
    shippoInfo &&
    shippoInfo.carrier === 'ups' &&
    shippoInfo.servicelevel &&
    shippoInfo.servicelevel.token === 'ups_surepost'
  );
};

export const cleanUndefinedPropertiesFromObject = (data) => {
  return Object.keys(data).reduce((acc, key) => {
    if (data[key]) {
      acc[key] = data[key];
    }

    return acc;
  }, {});
};

export const getChannelIconSrc = (ticket, isReviewTicket, isSelected, isValidationTicket) => {
  if (isValidationTicket) {
    return '/images/new-designs/queue-component/validate-ticket-icon.svg';
  }

  if (isReviewTicket) {
    return '/images/conversationIcons/review-ticket-icon.svg';
  }

  if (isDelayedAsyncChat(ticket)) {
    return '/images/conversationIcons/delayed-async-chat-or-sms.svg';
  }

  if (isAsyncChat(ticket)) {
    return '/images/conversationIcons/realtime-async-chat-or-sms.svg';
  }

  if (isVoiceCallChat(ticket) && !isSelected) {
    return '/images/conversationIcons/voice-call-icon.svg';
  }

  if (isSelected) {
    if (isVoiceCallChat(ticket)) {
      return '/images/conversationIcons/selected-voice-call-icon.svg';
    }

    if (isLiveChat(ticket.communicationType)) {
      return '/images/conversationIcons/selected-chat-icon.svg';
    }

    return '/images/conversationIcons/selected-email-icon.svg';
  }

  if (!isAnswered(ticket)) {
    if (isLiveChat(ticket.communicationType)) {
      return '/images/conversationIcons/unanswered-chat-icon.svg';
    }

    if (ticket.communicationType === validCommunicationType.TWITTER_DM) {
      return '/images/conversationIcons/unanswered-twitter-icon.svg';
    }

    return '/images/conversationIcons/unanswered-unselected-email-icon.svg';
  }

  if (isVoiceCallChat(ticket)) {
    return '/images/conversationIcons/answered-voice-call-icon.svg';
  }

  if (isLiveChat(ticket.communicationType)) {
    return '/images/conversationIcons/answered-chat-icon.svg';
  }

  if (ticket.communicationType === validCommunicationType.TWITTER_DM) {
    return '/images/conversationIcons/answered-twitter-icon.svg';
  }

  return '/images/conversationIcons/answered-email-icon.svg';
};

export const wrapVariableInArrayIfNotArrayAlready = (variable) => {
  if (variable) {
    if (Array.isArray(variable)) {
      return variable;
    }

    return [variable];
  }

  return [];
};

export const parseSanitizedHtml = (html) => {
  return {
    dangerouslySetInnerHTML: { __html: sanitizeHtml(html) },
  };
};

export const replaceHtmlEntities = (text) => {
  text = text.replace(regex.nbsp, '<br>');
  text = text.replace(regex.lineBreakTag, '\n');
  text = text.replace(regex.leftArrow, '<');
  text = text.replace(regex.rightArrow, '>');
  text = text.replace(regex.amp, '&');

  return text;
};

export const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const setAnchorElementsTarget = (html, anchorTarget = '_blank') => {
  const validTargets = ['_blank', '_parent', '_self', '_top'];
  if (typeof html !== 'string' || !validTargets.includes(anchorTarget)) {
    return html;
  }

  try {
    const tempDiv = window.document.createElement('div');
    if (tempDiv) {
      tempDiv.innerHTML = html;
      const anchors = tempDiv.getElementsByTagName('a');
      if (anchors) {
        for (let i = 0; i < anchors.length; i++) {
          anchors[i].setAttribute('target', anchorTarget);
          anchors[i].setAttribute('rel', 'noopener noreferrer');
        }
        html = tempDiv.innerHTML.substring(0);
      }
    }
  } catch (error) {
    // This would be only a minor issue, will keep this output only in non-production environments.
    handleError({ error });
  }

  return html;
};

const clientSupportsHtml = (ticketType, communicationType) => {
  return !isLiveChat(communicationType);
};

export const messageToClientFormat = (messageText, ticketType, communicationType) => {
  if (clientSupportsHtml(ticketType, communicationType)) {
    return sanitizeHtml(messageText);
  }

  return htmlToText(messageText);
};

export const sanitizeHtml = (html) => {
  html = html || '';
  let sanitized = xss(html, {
    whiteList: {
      a: ['href', 'title', 'target', 'rel'],
      img: ['src', 'width', 'height', 'style'], // The 'alt' attribute causes a match with black listed words and is not really needed.
      p: [],
      br: [],
    },
    stripIgnoreTag: true, // filter out all HTML not in the whilelist
    stripIgnoreTagBody: ['script'], // the script tag is a special case, we need
    // to filter out its content
  });
  sanitized = sanitized.trim();
  sanitized = setAnchorElementsTarget(sanitized);

  return sanitized;
};

export const htmlToText = (html) => {
  html = html || '';
  try {
    const text = sanitizeHtml(html)
      .replace(regex.imageTag, (match) => match.match(regex.imageSrc)[1])
      .replace(regex.aTag, (match) => {
        const href = match.match(regex.aHref)[1];
        const word = match.match(regex.aTagWord)[1];

        return word === href ? `${word}` : `${word} (${href})`;
      })
      .replace(regex.openParagraphTag, '')
      .replace(regex.closeParagraphTag, '\n\n')
      .replace(regex.lineBreakTag, '\n')
      .replace(regex.nbsp, ' ')
      .replace(regex.leftArrow, '<')
      .replace(regex.rightArrow, '>')
      .replace(regex.amp, '&')
      .trim();

    return text;
  } catch (error) {
    handleError({ error });

    return html;
  }
};

export const getErrorMessage = (message) => {
  if (!message) {
    return 'Something went wrong';
  }

  if (typeof message === 'string') {
    return message;
  }

  if (message.message) {
    return getErrorMessage(message.message);
  }
};

export const isPartnerWhitelisted = (isSpecialistLite, whitelist = [], partnerSimplrAccountId) => {
  if (!isSpecialistLite) {
    return true;
  }

  // return false for Asurion tickets
  if (partnerSimplrAccountId === 'JMkJZWdaCS') {
    return false;
  }

  return whitelist.includes(partnerSimplrAccountId);
};

export const mapZendeskTicektsToAlgoliaFormat = (zendeskTickets) => {
  if (isEmpty(zendeskTickets.results)) {
    return null;
  }

  const results = zendeskTickets.results;
  const highlights = zendeskTickets.highlights.results;

  const reformatedTicketsResults = results.map((result, idx) => {
    const highlightedSubjectsAndMessages = {
      subjects: [
        {
          value: highlights[idx].subject || result.subject,
          matchLevel: 'full',
        },
      ],
      messages: [
        {
          value: get(highlights, `${idx}.description.0`, null) || result.description,
          matchLevel: 'full',
        },
      ],
    };

    return {
      platformTicketId: result.id,
      firstMessageArrivedAt: result.created_at,
      subjects: [result.subject],
      messages: [result.description],
      directions: [],
      _snippetResult: highlightedSubjectsAndMessages,
      _highlightResult: highlightedSubjectsAndMessages,
    };
  });

  return reformatedTicketsResults;
};

export const getTimezoneList = () => {
  return moment.tz
    .names()
    .reduce((memo, tz) => {
      memo.push({
        name: tz,
        offset: moment.tz(tz).utcOffset(),
      });

      return memo;
    }, [])
    .sort((a, b) => a.offset - b.offset)
    .reduce((memo, tz) => {
      const timezone = moment.tz(tz.name).format('Z');

      return [...memo, { id: tz.name, value: `(GMT${timezone}) ${tz.name}`, label: `(GMT${timezone}) ${tz.name}` }];
    }, []);
};

export const listToTree = (list) => {
  const map = {};
  const roots = [];
  let node;

  for (let i = 0; i < list.length; i += 1) {
    map[list[i].value] = i;
    list[i].children = null;
  }

  for (let i = 0; i < list.length; i += 1) {
    node = list[i];
    if (!!node.parentId) {
      if (!list[map[node.parentId]].children) {
        list[map[node.parentId]].children = [];
      }

      list[map[node.parentId]].children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
};

export const getRandomString = (length) => {
  return [...Array(length)].map(() => (~~(Math.random() * 36)).toString(36)).join('');
};

export const getSessionTokenHeader = async () => ({ 'x-session-token': getSessionToken() });

export const convertDollarsToCents = (price) => price * 100;

export const doesContainEmojis = (message = '') => {
  if (!message) {
    return false;
  }

  const emojiRegExp = new RegExp(`[${ALLOWED_TEXT_SYMBOLS.join('')}]`, 'gmu');

  return new RegExp(/\p{Extended_Pictographic}/gu).test(message.replace(emojiRegExp, ''));
};

export function camelToSnakeCase(str) {
  return str.replace(/[A-Z]/g, (letter, index) => {
    return index === 0 ? letter.toLowerCase() : '_' + letter.toLowerCase();
  });
}

/**
 * Utility to generate a single object with the react-select structure required byt the library.
 * Each key has a function as a value, so we need to "merge" this 2 styles into one, preserving
 * the baseStyles parameter and not lose any style coming from the library itself.
 *
 * For reference on how styles work for react-select:
 * https://react-select.com/styles
 *
 */
export const generateReactSelectStyles = (rcSelectA, rcSelectB) => {
  const composedStyle = { ...rcSelectA };

  Object.getOwnPropertyNames(rcSelectB).forEach((property) => {
    if (Object.hasOwn(rcSelectA, property)) {
      composedStyle[property] = (baseStyles, state) => ({
        ...rcSelectA[property](baseStyles, state),
        ...rcSelectB[property](baseStyles, state),
      });
    } else {
      composedStyle[property] = rcSelectB[property];
    }
  });

  return composedStyle;
};
